import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import { useLocation } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Person,
  People as PeopleIcon,
  MoneyOff as MoneyOffIcon,
  Language as GlobeIcon,
} from "@material-ui/icons";

import { FleetContext } from "../../context";
import { Brand } from "../Icons/Brand";
import Scooter from "../Icons/Scooter";
import VerticalNavItem from "./VerticalNavItem";
import Permissions from "../../permissions";
import PermissionedDisplay from "../Layout/PermissionedDisplay";
import { useTestIds } from "../../hooks/useTestIds";

const useStyles = makeStyles((theme) => ({
  nav: {
    height: "100%",
    width: theme.spacing(theme.navSpacingSize),
    position: "fixed",
    zIndex: "1",
    top: "0",
    left: "0",
    backgroundColor: theme.palette.background.paper,
    // overflowX: "hidden",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flexStart",
    margin: "0",
    padding: "0",
    listStyle: "none",
  },
}));

const VerticalNav = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const { fleets, activeFleet } = useContext(FleetContext);
  const location = useLocation();
  const testIds = useTestIds("vertical-nav");

  const fleetMenuItems = fleets.map((f) => ({
    label: f.name,
    path: `/fleets/${f.uuid4}`,
    isSelected: location.pathname.includes(f.uuid),
  }));

  fleetMenuItems.push({
    label: t("HeaderNav.NewFleet"),
    path: "/fleets/new",
    isSelected: location.pathname.includes("/fleets/new"),
    permissions: [Permissions.writeFleets, Permissions.writeRatePlans],
    isSecondary: true,
  });
  const userMenuItems = [{ label: t("HeaderNav.Logout"), path: "/logout" }];

  return (
    <nav className={classes.nav} aria-label={t("HeaderNav.MainMenu")}>
      <ul data-testid={testIds.wrapper} className={classes.list}>
        <VerticalNavItem
          background={theme.palette.primary.red}
          linkPath="/"
          ariaLabel={t("HeaderNav.Home")}
          testId={testIds.logo}
        >
          <Brand client="superpedestrian" />
        </VerticalNavItem>
        <VerticalNavItem
          selected={location.pathname.startsWith("/fleets")}
          menu={fleetMenuItems}
          autocomplete
          linkPath="/fleets"
          ariaLabel={t("HeaderNav.Fleets")}
          testId={testIds.fleets}
        >
          {activeFleet?.abbreviation ?? <GlobeIcon fontSize="large" />}
        </VerticalNavItem>
        <VerticalNavItem
          selected={location.pathname.startsWith("/vehicles")}
          linkPath="/vehicles"
          ariaLabel={t("HeaderNav.Vehicles")}
          testId={testIds.vehicles}
        >
          <Scooter fontSize="large" />
        </VerticalNavItem>
        <PermissionedDisplay permission={Permissions.readRidersAdmin}>
          <VerticalNavItem
            selected={location.pathname.startsWith("/riders")}
            linkPath="/riders"
            ariaLabel={t("HeaderNav.Riders")}
            testId={testIds.riders}
          >
            <PeopleIcon fontSize="large" />
          </VerticalNavItem>
        </PermissionedDisplay>
        <PermissionedDisplay permission={Permissions.readPromos}>
          <VerticalNavItem
            selected={location.pathname.startsWith("/promos")}
            linkPath="/promos"
            ariaLabel={t("HeaderNav.Promos")}
            testId={testIds.promos}
          >
            <MoneyOffIcon fontSize="large" />
          </VerticalNavItem>
        </PermissionedDisplay>
        <VerticalNavItem
          menu={userMenuItems}
          ariaLabel={t("HeaderNav.User")}
          testId={testIds.loggedInUser}
        >
          <Person fontSize="large" />
        </VerticalNavItem>
      </ul>
    </nav>
  );
};

export default VerticalNav;
